import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { 
  MapPin, 
  Cloud, 
  Users, 
  ArrowRight, 
  Phone, 
  CheckCircle, 
  ChevronRight,
  AlertTriangle,
  Home,
  FileText
} from "lucide-react";
import { Button } from "@/components/ui/button";
import LeadForm from "@/components/LeadForm";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";

import useSEO from "@/hooks/useSEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CityPage() {
  const { slug } = useParams();
  const [city, setCity] = useState(null);
  const [allCities, setAllCities] = useState([]);
  const [relatedFaqs, setRelatedFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [cityRes, citiesRes, faqsRes] = await Promise.all([
          axios.get(`${API}/cities/${slug}`),
          axios.get(`${API}/cities`),
          axios.get(`${API}/faqs?category=storm-damage`),
        ]);
        setCity(cityRes.data);
        setAllCities(citiesRes.data);
        setRelatedFaqs(faqsRes.data.slice(0, 5));
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          if (process.env.NODE_ENV === 'development') {
            console.error('Failed to fetch city data:', err);
          }
        }
        setError("City not found");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug]);

  // Use SEO hook with city data
  useSEO(city);

  if (loading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (error || !city) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-heading text-2xl font-semibold text-navy mb-4">
            City Not Found
          </h1>
          <Link to="/service-areas">
            <Button className="bg-orange hover:bg-orange-hover text-white">
              View All Service Areas
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const nearbyCities = allCities.filter((c) =>
    city.nearby_cities.includes(c.slug)
  );

  // Generate schema markup
  const schemaMarkup = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": `Minnesota Storm Damage Help - ${city.name}`,
    "description": city.intro,
    "areaServed": {
      "@type": "City",
      "name": city.name,
      "containedInPlace": {
        "@type": "State",
        "name": "Minnesota"
      }
    },
    "serviceType": ["Storm Damage Assessment", "Roofing Contractor Connection", "Insurance Claim Guidance"]
  };

  return (
    <div data-testid={`city-page-${slug}`} className="">
      {/* Schema Markup */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaMarkup) }}
      />

      {/* Hero Section */}
      <section className="bg-navy py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {/* Breadcrumb */}
          <Breadcrumb className="mb-8">
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/" className="text-gray-400 hover:text-white">
                    Home
                  </Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator className="text-gray-600" />
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/service-areas" className="text-gray-400 hover:text-white">
                    Service Areas
                  </Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator className="text-gray-600" />
              <BreadcrumbItem>
                <BreadcrumbPage className="text-white">{city.name}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/10 text-white/90 text-sm font-medium px-4 py-2 rounded-full mb-6">
                <MapPin className="w-4 h-4 text-orange" />
                {city.region}
              </div>

              <h1
                data-testid="city-title"
                className="font-heading text-4xl sm:text-5xl font-semibold text-white tracking-tight mb-6"
              >
                {city.name} Storm Damage Help
              </h1>

              <p className="text-xl text-gray-300 leading-relaxed mb-8">
                {city.intro}
              </p>

              <a href="#city-lead-form">
                <Button
                  data-testid="city-cta-btn"
                  className="bg-orange hover:bg-orange-hover text-white px-8 py-4 text-lg rounded-lg"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  {city.cta_text}
                </Button>
              </a>
            </div>

            {/* Weather Note Card */}
            <div className="bg-navy-light border border-navy-border rounded-xl p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 bg-orange/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Cloud className="w-6 h-6 text-orange" />
                </div>
                <div>
                  <h3 className="font-heading text-lg font-semibold text-white mb-2">
                    Local Weather Patterns
                  </h3>
                  <p className="text-gray-400 leading-relaxed">
                    {city.weather_note}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-navy rounded-xl flex items-center justify-center flex-shrink-0">
                  <Users className="w-6 h-6 text-orange" />
                </div>
                <div>
                  <h3 className="font-heading text-lg font-semibold text-white mb-2">
                    Understanding {city.name} Homeowners
                  </h3>
                  <p className="text-gray-400 leading-relaxed">
                    {city.psychology}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="font-heading text-3xl font-semibold text-navy mb-8 text-center">
            How We Help {city.name} Homeowners
          </h2>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: AlertTriangle,
                title: "Storm Damage Assessment",
                description: `Not sure if recent weather affected your ${city.name} home? We help identify potential damage and determine the best next steps.`,
              },
              {
                icon: Home,
                title: "Contractor Connections",
                description: `Connect with licensed, reputable roofing contractors who serve ${city.name} and understand local building requirements.`,
              },
              {
                icon: FileText,
                title: "Insurance Guidance",
                description: "Navigate the claims process with professionals experienced in storm restoration and insurance documentation.",
              },
            ].map((service) => (
              <div
                key={service.title}
                className="bg-white border border-gray-200 rounded-xl p-8 hover:border-orange/30 transition-colors"
              >
                <div className="w-12 h-12 bg-orange/10 rounded-xl flex items-center justify-center mb-6">
                  <service.icon className="w-6 h-6 text-orange" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-navy mb-3">
                  {service.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Extended Content Section - Storm Damage Types */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-heading text-3xl font-semibold text-navy mb-6">
              Types of Storm Damage Affecting {city.name} Homes
            </h2>
            <p className="text-gray-600 leading-relaxed mb-8">
              Minnesota weather presents unique challenges for {city.name} homeowners. Understanding the different types of storm damage can help you identify potential issues and take appropriate action.
            </p>

            <div className="space-y-8">
              <div>
                <h3 className="font-heading text-xl font-semibold text-navy mb-3">Hail Damage</h3>
                <p className="text-gray-600 leading-relaxed">
                  Hail damage is one of the most common issues facing {city.name} homeowners after severe storms. Hail can range from pea-sized to golf ball-sized or larger, and even smaller hail can cause significant damage to roofing materials. Signs of hail damage include dented or bruised shingles, granule loss visible in gutters and downspouts, dented metal flashing, vents, and gutters, and cracked or broken shingles. Many {city.name} homeowners don't realize their roof has hail damage until a professional inspection reveals the extent of the problem.
                </p>
              </div>

              <div>
                <h3 className="font-heading text-xl font-semibold text-navy mb-3">Wind Damage</h3>
                <p className="text-gray-600 leading-relaxed">
                  High winds during Minnesota storms can cause substantial damage to roofing systems. Wind damage typically appears as lifted or curled shingles, missing shingles (often found in the yard or neighboring properties), exposed underlayment or decking, and damaged ridge caps. {city.name}'s {city.weather_note?.toLowerCase()} Wind damage often starts at vulnerable points like edges, corners, and ridgelines before spreading across the roof surface.
                </p>
              </div>

              <div>
                <h3 className="font-heading text-xl font-semibold text-navy mb-3">Fallen Branch and Debris Damage</h3>
                <p className="text-gray-600 leading-relaxed">
                  Trees and flying debris during storms can cause immediate and obvious damage to {city.name} homes. This includes punctured roofing materials, cracked or broken shingles, damaged flashing and ventilation, and structural damage in severe cases. Even if a fallen branch appears to have caused minimal damage, the impact can compromise the integrity of surrounding roofing materials.
                </p>
              </div>

              <div>
                <h3 className="font-heading text-xl font-semibold text-navy mb-3">Gutter and Siding Damage</h3>
                <p className="text-gray-600 leading-relaxed">
                  Storm damage often extends beyond the roof to affect gutters, downspouts, and siding. Dented or detached gutters, cracked or loose siding panels, and damage to exterior trim are common after severe weather events. These components work together with your roof to protect your home, so comprehensive assessment is important.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-heading text-3xl font-semibold text-navy mb-6">
              Why {city.name} Homeowners Choose Us
            </h2>
            <p className="text-gray-600 leading-relaxed mb-8">
              {city.psychology} That's exactly why we exist - to help homeowners like you navigate the storm damage process with confidence and connect with contractors who share those values.
            </p>

            <div className="grid sm:grid-cols-2 gap-6">
              {[
                "Licensed and insured Minnesota contractors",
                "No high-pressure sales tactics",
                "Insurance claim expertise",
                "Local knowledge of {city.name} building codes",
                "Focus on education before decisions",
                "No cost or obligation to homeowners",
                "Trusted by insurance professionals",
                "Quality workmanship guarantee"
              ].map((item) => (
                <div key={item} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-orange flex-shrink-0 mt-1" />
                  <span className="text-gray-700">{item.replace('{city.name}', city.name)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Insurance Process Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-heading text-3xl font-semibold text-navy mb-6">
              Storm Damage Insurance Claims in {city.name}
            </h2>
            <p className="text-gray-600 leading-relaxed mb-6">
              Many {city.name} homeowners find the insurance claims process confusing or overwhelming. Understanding how the process works can help you make better decisions and potentially reduce your out-of-pocket costs.
            </p>

            <div className="bg-gray-50 border border-gray-200 rounded-xl p-8 mb-8">
              <h3 className="font-heading text-lg font-semibold text-navy mb-4">
                The Typical Storm Damage Claim Process
              </h3>
              <ol className="space-y-4 text-gray-600">
                <li className="flex gap-4">
                  <span className="w-8 h-8 bg-orange/10 rounded-full flex items-center justify-center flex-shrink-0 text-orange font-semibold">1</span>
                  <div>
                    <strong className="text-navy">Document the Damage:</strong> Take photos of any visible damage to your roof, gutters, siding, and property immediately after the storm.
                  </div>
                </li>
                <li className="flex gap-4">
                  <span className="w-8 h-8 bg-orange/10 rounded-full flex items-center justify-center flex-shrink-0 text-orange font-semibold">2</span>
                  <div>
                    <strong className="text-navy">Contact Your Insurance Company:</strong> Report the damage promptly. Most policies require notification within a specific timeframe.
                  </div>
                </li>
                <li className="flex gap-4">
                  <span className="w-8 h-8 bg-orange/10 rounded-full flex items-center justify-center flex-shrink-0 text-orange font-semibold">3</span>
                  <div>
                    <strong className="text-navy">Get a Professional Assessment:</strong> Have a reputable contractor inspect and document the damage before or during the adjuster's visit.
                  </div>
                </li>
                <li className="flex gap-4">
                  <span className="w-8 h-8 bg-orange/10 rounded-full flex items-center justify-center flex-shrink-0 text-orange font-semibold">4</span>
                  <div>
                    <strong className="text-navy">Meet with the Adjuster:</strong> The insurance company will send an adjuster to assess the damage and determine coverage.
                  </div>
                </li>
                <li className="flex gap-4">
                  <span className="w-8 h-8 bg-orange/10 rounded-full flex items-center justify-center flex-shrink-0 text-orange font-semibold">5</span>
                  <div>
                    <strong className="text-navy">Review and Complete Repairs:</strong> Once approved, work with your chosen contractor to complete repairs and recover any depreciation.
                  </div>
                </li>
              </ol>
            </div>

            <p className="text-gray-600 leading-relaxed">
              If your claim covers the necessary repairs, your out-of-pocket cost may be limited to your deductible plus any upgrades you choose. Working with contractors experienced in insurance restoration can help ensure all damage is properly documented and covered.
            </p>
          </div>
        </div>
      </section>

      {/* FAQs Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <h2 className="font-heading text-3xl font-semibold text-navy">
              Common Questions
            </h2>
            <Link
              to="/faqs"
              className="text-orange hover:text-orange-hover font-medium flex items-center gap-2"
            >
              View All FAQs
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="space-y-4">
            {relatedFaqs.map((faq) => (
              <div
                key={faq.id || faq.question}
                className="bg-gray-50 border border-gray-200 rounded-lg p-6"
              >
                <h3 className="font-heading font-semibold text-navy mb-2">
                  {faq.question}
                </h3>
                <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lead Form Section */}
      <section
        id="city-lead-form"
        className="py-16 bg-gray-50"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-navy mb-4">
                Get Help in {city.name}
              </h2>
              <p className="text-gray-600 text-lg leading-relaxed mb-8">
                Whether you're dealing with recent storm damage or just want peace of
                mind about your roof's condition, we're here to help {city.name}{" "}
                homeowners connect with the right professionals.
              </p>

              <div className="space-y-4 mb-8">
                {[
                  "Licensed Minnesota contractors",
                  "No obligation consultation",
                  "Local expertise for your area",
                  "Insurance claim assistance available",
                ].map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-orange flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <LeadForm />
          </div>
        </div>
      </section>

      {/* Nearby Cities Section */}
      {nearbyCities.length > 0 && (
        <section className="py-16 bg-navy">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <h2 className="font-heading text-3xl font-semibold text-white mb-8 text-center">
              Nearby Service Areas
            </h2>

            <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4">
              {nearbyCities.map((nearbyCity) => (
                <Link
                  key={nearbyCity.slug}
                  to={`/service-areas/${nearbyCity.slug}`}
                  className="group bg-navy-light border border-navy-border rounded-lg p-5 hover:border-orange/30 transition-all"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-medium text-white group-hover:text-orange transition-colors">
                        {nearbyCity.name}
                      </span>
                      <span className="block text-gray-500 text-sm mt-1">
                        {nearbyCity.region}
                      </span>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-500 group-hover:text-orange group-hover:translate-x-1 transition-all" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
